import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import axios, { API } from "@/lib/api";
import { Loader2, Mail, Lock, User as UserIcon, CheckCircle2, Sparkles } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import AuthShell from "@/components/AuthShell";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { formatApiErrorDetail, validatePassword } from "@/lib/authForms";
import { captureReferralFromUrl, getStoredReferralCode, clearReferralCookie } from "@/lib/vendorReferral";

export default function SignUp() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [referralCode, setReferralCode] = useState(null);
  // Vendor business_name (public + safe) — populated when a valid ref
  // cookie is present. Renders the "You're joining via <biz>" badge.
  const [referralDisplayName, setReferralDisplayName] = useState(null);

  useEffect(() => {
    if (!loading && user) navigate("/app", { replace: true });
  }, [user, loading, navigate]);

  // Capture ?ref=CODE from URL → 30-day cookie for attribution + fetch
  // the vendor's display_name so we can render the badge. The App-level
  // ReferralCatcher may have already cached the cookie on a prior
  // route, so we ALSO check the cookie directly and re-fetch the
  // display_name for the badge.
  useEffect(() => {
    (async () => {
      const codeFromUrl = await captureReferralFromUrl();
      const code = codeFromUrl || getStoredReferralCode();
      if (!code) return;
      setReferralCode(code);
      // Fetch display_name (backend returns null for suspended vendors).
      try {
        const { data } = await axios.get(`${API}/vendor/public/referral/${code}`);
        if (data?.valid && data?.display_name) {
          setReferralDisplayName(data.display_name);
        }
      } catch (_e) {
        // Silent — badge just won't show.
      }
    })();
  }, []);

  const pwIssues = validatePassword(form.password);
  const canSubmit = form.email && pwIssues.length === 0 && !submitting;

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);
    // Funnel top — fires once per submit regardless of outcome. GA4
    // sees intent-to-signup even when validation fails downstream, so
    // we can compute submit → verified-user drop-off later.
    trackEvent("signup_started", { method: "email" });
    try {
      await axios.post(`${API}/auth/signup`, {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim() || undefined,
        ref: getStoredReferralCode() || undefined,
      });
      clearReferralCookie();
      setSentTo(form.email.trim().toLowerCase());
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, "Could not create account."));
    } finally {
      setSubmitting(false);
    }
  };

  if (sentTo) {
    return (
      <AuthShell
        title="Check your inbox"
        subtitle={`We sent a verification link to ${sentTo}. Open it to activate your studio.`}
        footer={
          <span>
            Already verified?{" "}
            <Link to="/signin" data-testid="signup-go-signin" className="text-[#D4AF37] hover:text-[#F3CE5A]">
              Sign in →
            </Link>
          </span>
        }
      >
        <div className="mt-8 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-5 text-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-300 mx-auto mb-2" />
          <p className="text-sm text-white/80">The link expires in 24 hours.</p>
          <p className="text-xs text-white/45 mt-2 leading-relaxed">
            Didn't get it? Check spam, or try again in a few minutes.
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Create your account"
      subtitle="Sign up in seconds. Verify your email, then start designing with 10 free credits."
      footer={
        <span>
          Already have an account?{" "}
          <Link to="/signin" data-testid="signup-go-signin" className="text-[#D4AF37] hover:text-[#F3CE5A]">
            Sign in →
          </Link>
        </span>
      }
    >
      <form onSubmit={handleSubmit} className="mt-7 space-y-4" data-testid="signup-form">
        {referralDisplayName && (
          <div
            className="rounded-xl border border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/10 via-[#1A1306] to-[#0E0E0E] px-4 py-3 flex items-center gap-3"
            data-testid="signup-referral-badge"
          >
            <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Referred by</div>
              <div className="text-sm text-white truncate">
                <strong>{referralDisplayName}</strong>{" "}
                <span className="text-white/50">— thanks for the referral!</span>
              </div>
            </div>
          </div>
        )}
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">Name <span className="text-white/30 normal-case">(optional)</span></label>
          <div className="relative">
            <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <input
              type="text"
              value={form.name}
              onChange={onChange("name")}
              maxLength={80}
              autoComplete="name"
              data-testid="signup-name-input"
              className="w-full bg-[#141414] border border-white/10 rounded-xl pl-9 pr-3 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              placeholder="Your name"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <input
              type="email"
              value={form.email}
              onChange={onChange("email")}
              required
              autoComplete="email"
              data-testid="signup-email-input"
              className="w-full bg-[#141414] border border-white/10 rounded-xl pl-9 pr-3 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              placeholder="you@studio.com"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <input
              type="password"
              value={form.password}
              onChange={onChange("password")}
              required
              autoComplete="new-password"
              data-testid="signup-password-input"
              className="w-full bg-[#141414] border border-white/10 rounded-xl pl-9 pr-3 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              placeholder="Min 8 chars · letters + numbers"
            />
          </div>
          {form.password && pwIssues.length > 0 && (
            <p className="text-[10px] text-amber-300/80 mt-1.5">Password needs: {pwIssues.join(", ")}.</p>
          )}
        </div>

        {error && (
          <p data-testid="signup-error" className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="signup-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3.5 hover:bg-[#F3CE5A] transition disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_30px_-8px_rgba(212,175,55,0.55)]"
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating account…</> : <>Create account</>}
        </button>

        <p className="text-[10px] text-white/40 text-center leading-relaxed">
          By creating an account you agree to our{" "}
          <Link to="/terms" className="text-white/60 hover:text-[#D4AF37]">Terms</Link>{" "}and{" "}
          <Link to="/privacy" className="text-white/60 hover:text-[#D4AF37]">Privacy Policy</Link>.
        </p>
      </form>

      <div className="my-7 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-white/30">
        <div className="flex-1 h-px bg-white/10" /> or <div className="flex-1 h-px bg-white/10" />
      </div>

      <GoogleAuthButton next="/app" label="Sign up with Google" />
    </AuthShell>
  );
}
